/**
 * Subscription hook
 * Provides subscription state to the entire app via TanStack Query
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { subscriptionKeys } from '@/lib/query-keys'
import { fetchSubscriptionStatus } from '@/lib/api/subscription'
import type { SubscriptionStatus } from '@/types/database'
import { useCallback } from 'react'

const FREE_HABIT_LIMIT = 6
const FREE_VERIFICATION_LIMIT = 14
const SUBSCRIPTION_CACHE_KEY = 'miac_subscription_cache'

function getCachedSubscription(): SubscriptionStatus | null {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
    if (!cached) return null
    return JSON.parse(cached) as SubscriptionStatus
  } catch {
    return null
  }
}

function cacheSubscription(status: SubscriptionStatus): void {
  try {
    localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(status))
  } catch {
    // localStorage might be full or disabled
  }
}

/**
 * Clear the subscription cache (localStorage + TanStack Query)
 * Call on sign-out, account switch, or any auth state change
 */
export function clearSubscriptionCache(): void {
  try {
    localStorage.removeItem(SUBSCRIPTION_CACHE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Hook to fetch and cache subscription status
 * Automatically refetches when the app comes to foreground
 */
export function useSubscription() {
  const query = useQuery({
    queryKey: subscriptionKeys.status(),
    queryFn: async () => {
      const status = await fetchSubscriptionStatus()
      cacheSubscription(status)
      return status
    },
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: 5 * 60 * 1000, // Poll every 5 min to catch refunds, expirations, payment failures
    retry: 2,
    placeholderData: getCachedSubscription() ?? undefined,
  })

  const status = query.data

  return {
    ...query,
    // True when we have real server data (not just localStorage placeholder)
    isSubscriptionReady: !query.isPlaceholderData && !query.isLoading,
    // Convenience getters
    isPremium: status?.isPremium ?? false,
    tier: status?.tier ?? 'free',
    subscriptionStatus: status?.status ?? null,
    provider: status?.provider ?? null,
    expiresAt: status?.expiresAt ?? null,
    // Verification info
    freeVerificationsUsed: status?.freeVerificationsUsed ?? 0,
    freeVerificationsRemaining: status?.freeVerificationsRemaining ?? FREE_VERIFICATION_LIMIT,
    freeVerificationsLimit: status?.freeVerificationsLimit ?? FREE_VERIFICATION_LIMIT,
    purchasedVerifications: status?.purchasedVerifications ?? 0,
    totalVerificationsRemaining: status?.totalVerificationsRemaining ?? FREE_VERIFICATION_LIMIT,
    // Limits
    habitLimit: (status?.isPremium) ? Infinity : FREE_HABIT_LIMIT,
    canVerify: (status?.isPremium) || (status?.totalVerificationsRemaining ?? FREE_VERIFICATION_LIMIT) > 0,
  }
}

/**
 * Hook to refresh subscription status
 * Useful after a purchase or restore
 */
export function useRefreshSubscription() {
  const queryClient = useQueryClient()
  
  return useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: subscriptionKeys.status() })
  }, [queryClient])
}

/**
 * Hook to check if user can create more habits
 * Returns { canCreate, currentCount, limit, remaining }
 */
export function useHabitLimit(currentHabitCount: number) {
  const { isPremium, habitLimit } = useSubscription()

  return {
    canCreate: isPremium || currentHabitCount < FREE_HABIT_LIMIT,
    currentCount: currentHabitCount,
    limit: habitLimit,
    remaining: isPremium ? Infinity : Math.max(0, FREE_HABIT_LIMIT - currentHabitCount),
    isPremium,
  }
}

/**
 * Utility to get a human-readable subscription label
 */
export function getSubscriptionLabel(status: SubscriptionStatus | undefined): string {
  if (!status) return 'Free'
  if (!status.isPremium) return 'Free'
  
  if (status.status === 'canceled') return 'Premium (canceling)'
  if (status.status === 'past_due') return 'Premium (payment issue)'
  if (status.status === 'trialing') return 'Premium Trial'
  return 'Premium'
}

export { FREE_HABIT_LIMIT, FREE_VERIFICATION_LIMIT }
